import {ChangeDetectionStrategy, Component} from '@angular/core';
import {numericalOperatorsTooltip, regexOperatorsTooltip, SearchInputGroup,} from "../search-input/search-input.model";

@Component({
  selector: 'asax-header-search-container',
  templateUrl: './search-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchContainerComponent {
  readonly searchInputGroups: SearchInputGroup[] = [
    {
      id: 'position',
      title: 'Position',
      icon: 'compass',
      searchInputs: [
        {
          id: 'sourceNameResolver',
          legacyId: 'source_name_resolver',
          label: 'Source name',
          type: 'source-resolver',
          info: 'Search for a source name using the Sesame name resolver (wildcards are not supported)',
          description: 'Use the Sesame service (combining a search of NED, Simbad and VizieR) to parse names commonly found throughout literature and return the coordinates.<br>A search <strong>radius</strong> in arcmin can be added to the end separated by a comma. The default search radius is 10 arcmin. All observations that have footprints overlapping with the search cone will be returned.',
          examples: ['Cen A', 'M83', 'HUDF, 30'],
          elasticParserType: 'source-resolver'
        },
        {
          id: 'sourceName',
          legacyId: 'source_name_alma',
          label: 'ALMA source name',
          type: 'typeahead',
          info: 'ALMA source name',
          description: 'Name of the ALMA source name as given by the PI and as registered in the observed data',
          examples: ['Centaurus_A', '"R Scl"', 'NGC*'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'observations',
          typeaheadKeyword: 'sourceName',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'raDec',
          legacyId: 'ra_dec',
          label: 'RA Dec',
          type: 'text',
          info: 'Right Ascension and Declination',
          description: 'Coordinate search with default radius of 10 arcmin or coordinate-range search. RA and Dec may be expressed in sexagesimal or in decimal degrees. An alternative search <b>radius</b> in arcmin can be added to the end separated by a comma. All observations that have footprints overlapping with the search region will be returned.',
          units: 'Sexagesimal',
          examples: ['13:37:00.89 -29:51:59.8', '83.633075 22.014494', '04:31:38.425 18:13:57.242, 5', '181..185 >-0.1928'],
          elasticParserType: 'ra-dec',
          elasticValidatorType: 'ra-dec',
          tooltip: numericalOperatorsTooltip
        },
        {
          id: 'galactic',
          label: 'Galactic',
          type: 'text',
          info: 'Galactic longitude and Galactic latitude',
          description: 'Galactic longitude and Galactic latitude search or coordinate-range search. A search radius in degrees can be added to the end separated by a comma. The default search radius is 10 arcminutes.',
          units: 'Degrees',
          examples: ['172.10397239 -51.93358710', '279.7618..287.6429 >60.3661'],
          elasticParserType: 'galactic',
          elasticValidatorType: 'galactic',
          tooltip: numericalOperatorsTooltip
        },
        {
          id: 'targetList',
          label: 'Target List',
          type: 'target-list',
          info: 'Search for an entire list of source names or coordinates by uploading a text file.',
          description: 'A text file with source names and/or positions can be loaded into the interface and the query is executed for each of them in turn. Each source must be placed on a separate line. All the options available in the RA Dec input field are supported. Constraints in the other input fields are taken into account.',
          elasticParserType: 'target-list'
        },
        {
          id: 'spatialResolution',
          legacyId: 'spatial_resolution',
          label: 'Angular Resolution',
          type: 'text',
          info: 'Estimated average angular resolution of the observation.',
          description: 'The estimation is based on the 10% of the longest projected baseline-lenghts of the observation. The average value of the maximum and minimum angular resolutions of all spectral windows is used.',
          units: 'arcsec',
          examples: ['< 0.1', '0.1..2'],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          tooltip: numericalOperatorsTooltip
        },
        {
          id: 'largestAngularScale',
          label: 'Max. Recoverable Scale',
          type: 'text',
          info: 'Estimated largest angular scale on which features can be detected reliably.',
          description: 'Due to the fact that radio antennas can not be placed infinitely close, measurements do have a smallest separation which translates into a maximal angular distance beyond which features can not reliably be detected any more. Adding observations with the ALMA Total Power array can add those missing largest scales. The estimation is based on the 10% of the shortest projected baseline-lengths of the observation.',
          units: 'arcsec',
          examples: ['< 5', '100..200'],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          tooltip: numericalOperatorsTooltip
        }
      ]
    },
    {
      id: 'energy',
      title: 'Energy',
      icon: 'bolt-lightning',
      searchInputs: [
        {
          id: 'frequency',
          label: 'Frequency',
          type: 'text',
          info: 'Sky frequency',
          description: 'Observed frequency with respect to the Kinematic Local Standard of Rest (LSRK). If a single frequency value is entered, then all observations with spectral windows covering this frequency are returned. If a frequency range is given, then all observations with spectral windows overlapping that frequency range are returned.',
          units: 'GHz',
          examples: ['>100', '100..300', '100..300 | 600..900'],
          elasticParserType: 'frequency',
          elasticValidatorType: 'number',
          tooltip: numericalOperatorsTooltip
        },
        {
          id: 'band',
          legacyId: 'band_list',
          label: 'Band',
          type: 'dropdown',
          info: 'ALMA Receiver Band',
          description: 'List of ALMA Receiver Bands, i.e. frequency regions covered by the different ALMA Receivers.',
          dropdownItems: [
            {value: '1', text: '1', info: '35..50 GHz'},
            {value: '3', text: '3', info: '84..116 GHz'},
            {value: '4', text: '4', info: '125..163 GHz'},
            {value: '5', text: '5', info: '163..211 GHz'},
            {value: '6', text: '6', info: '211..275 GHz'},
            {value: '7', text: '7', info: '275..373 GHz'},
            {value: '8', text: '8', info: '385..500 GHz'},
            {value: '9', text: '9', info: '602..720 GHz'},
            {value: '10', text: '10', info: '787..950 GHz'}
          ],
          elasticParserType: 'bands'
        },
        {
          id: 'spectralResolution',
          legacyId: 'spectral_resolution',
          label: 'Spectral resolution',
          type: 'text',
          info: 'Estimated frequency resolution.',
          description: 'Estimated median frequency resolution of all the spectral windows using the channel widths as resolution element.',
          units: 'kHz',
          examples: ['<70', '>1e6', '2e6..3e6'],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          tooltip: numericalOperatorsTooltip
        },
        {
          id: 'continuumSensitivity',
          legacyId: 'continuum_sensitivity',
          label: 'Continuum sensitivity',
          type: 'text',
          info: 'Estimated noise in the aggregated continuum bandwidth.',
          description: 'Estimated noise in the aggregated continuum bandwidth. Note that this value only provides an indication. It does not include the effects of flagging or dynamic range limitations.',
          units: 'mJy/beam',
          examples: ['<0.5'],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          tooltip: numericalOperatorsTooltip
        },
        {
          id: 'lineSensitivity',
          legacyId: 'line_sensitivity',
          label: 'Line sensitivity (10 km/s)',
          type: 'text',
          info: 'Estimated noise for a 10km/s bandwidth.',
          description: 'Estimated noise in an nominal 10km/s bandwidth bin. Note that this value only provides an an indication. Effects of flagging or Hanning smoothing are not included, and a 10km/s bandwidth may not be achievable with the data as taken.',
          units: 'mJy/beam',
          examples: ['<2'],
          elasticParserType: 'number',
          elasticValidatorType: 'number',
          tooltip: numericalOperatorsTooltip
        }
      ]
    },
    {
      id: 'project',
      title: 'Project',
      icon: 'lightbulb',
      searchInputs: [
        {
          id: 'projectCode',
          legacyId: 'project_code',
          label: 'Project code',
          type: 'typeahead',
          info: 'Project code.',
          description: 'ALMA project code',
          examples: ['2011.0.00010.S', '2012.*', '2012*|2013*'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'projects',
          typeaheadKeyword: 'code',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'projectTitle',
          legacyId: 'project_title',
          label: 'Project Title',
          type: 'typeahead',
          info: 'ALMA Project Title',
          description: 'Title of the project as submitted by the PI',
          examples: ['*GALAXY*', '*?ebula*'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'projects',
          typeaheadKeyword: 'title',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'projectAbstract',
          legacyId: 'project_abstract',
          label: 'Project abstract',
          type: 'typeahead',
          info: 'Abstract text as submitted by the PI.',
          description: 'Case-insensitive full-text search on the project abstract text',
          examples: ['hydrogen fluoride', 'clusters of galaxies'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'projects',
          typeaheadKeyword: 'proposalAbstract',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'piName',
          legacyId: 'pi_name',
          label: 'PI Full Name',
          type: 'typeahead',
          info: 'ALMA PI name',
          description: 'Full name of the Principal Investigator of the project',
          examples: ['Carpenter, John', 'CAR*', 'Jo?n'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'projects',
          typeaheadKeyword: 'piName',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'proposalAuthors',
          legacyId: 'proposal_authors',
          label: 'Proposal authors',
          type: 'typeahead',
          info: 'Full name of CoIs.',
          description: 'Full names of the Co-Investigators',
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'projects',
          typeaheadKeyword: 'coiName',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'scienceKeywordIds',
          legacyId: 'science_keyword',
          label: 'Science keyword',
          type: 'science-keywords',
          info: 'Keywords of the proposal as provided by the PI',
          description: 'One or two keywords per project as selected by the PI in the ALMA Observing Tool upon proposal submission',
          elasticParserType: 'dropdown-items'
        }
      ]
    },
    {
      id: 'publication',
      title: 'Publication',
      icon: 'book',
      searchInputs: [
        {
          id: 'bibCode',
          label: 'BibCode',
          type: 'typeahead',
          info: 'The bibliography code of the publication.',
          description: 'Bibliography codes of refereed ALMA publications provided by <a href="https://ui.adsabs.harvard.edu/">ADS</a>',
          examples: ['2013ApJ...779...96T', '2020*', '2013ApJ...779...96T | 2016ApJ...828..100F'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'publications',
          typeaheadKeyword: 'bibCode',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'title',
          legacyId: 'pub_title',
          label: 'Publication Title',
          type: 'typeahead',
          info: 'Title of the project as submitted by the PI',
          description: 'Case-insensitive search through the title of the publication',
          examples: ['solar mass'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'publications',
          typeaheadKeyword: 'title',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'bibAbstract',
          legacyId: 'pub_abstract',
          label: 'Abstract',
          type: 'typeahead',
          info: 'Abstract of the publication',
          description: 'Case-insensitive full-text search through the abstract of the refereed publication',
          examples: ['maser'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'publications',
          typeaheadKeyword: 'bibAbstract',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'firstAuthor',
          legacyId: 'first_author',
          label: 'First Author',
          type: 'typeahead',
          info: 'First author of the publication',
          description: 'The first author as provided by telbib.eso.org',
          examples: ['Carpenter, John M.'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'publications',
          typeaheadKeyword: 'firstAuthor',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'authors',
          legacyId: 'authors',
          label: 'Authors',
          type: 'typeahead',
          info: 'Publication authors',
          description: 'Full list of first author and all co-authors.',
          examples: ['Carpenter', 'Cox|Dougherty'],
          elasticParserType: 'regex',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'publications',
          typeaheadKeyword: 'authors',
          tooltip: regexOperatorsTooltip
        }
      ]
    },
    {
      id: 'observation',
      title: 'Observation',
      icon: 'eye',
      searchInputs: [
        {
          id: 'observationDate',
          legacyId: 'start_date',
          label: 'Observation Date',
          type: 'text',
          info: 'Start of the first data-taking of the observation.',
          description: 'Start time and date of the first observing block containing the source in UTC.',
          examples: ['>2018-01-01', '<2018-12-31', '2018-01-01..2018-12-31'],
          elasticParserType: 'date',
          elasticValidatorType: 'date',
          tooltip: numericalOperatorsTooltip
        },
        {
          id: 'polarisationType',
          legacyId: 'polarisation_type',
          label: 'Polarisation Type',
          type: 'dropdown',
          info: 'Polarisation products.',
          description: 'Type of polarisation products provided',
          dropdownItems: [
            {value: '1', text: 'Single'},
            {value: '2', text: 'Dual'},
            {value: '4', text: 'Full'}
          ],
          elasticParserType: 'dropdown-items'
        },
        {
          id: 'mous',
          legacyId: 'member_ous_id',
          label: 'Member ous id',
          type: 'typeahead',
          info: 'Member ous id',
          description: 'Member ous id',
          examples: ['uid://A002/X639a2a/X2a', 'uid___A002_X5a9a13_X528'],
          elasticParserType: 'mous',
          elasticValidatorType: 'regex',
          typeaheadIndex: 'observations',
          typeaheadKeyword: 'mous',
          tooltip: regexOperatorsTooltip
        },
        {
          id: 'objectTypes',
          label: 'Object type',
          type: 'sky-objects',
          info: 'Only show ALMA observations for which <a href="http://simbad.u-strasbg.fr/simbad" target="_blank">SIMBAD</a> or <a href="https://ned.ipac.caltech.edu" target="_blank">NED</a> contain at least one object with the given object type in ALMA\'s observation footprint. If <b>\'Best match\'</b> is selected, then show those ALMA observations, where the selected object type matches the object type of the SIMBAD or NED source that has most likely been targeted by the ALMA PI.<br><strong class="text-danger">Warning:</strong> Use with great care! The object type identification is by construction incomplete. There will be false-positives and false-negatives. Identifications may change with time as is true for all scientific measurements. Also several categories may apply to a single object but only the main type is used here.',
          description: 'The SIMBAD or NED objects that fall within an ALMA observation\'s footprint are retrieved. That ALMA observation is shown in the result table in case any SIMBAD or NED object with the selected object type falls within the ALMA observation\'s footprint. ALMA observations mostly target a single object per footprint. We try to identify the \'best\' object out of the many SIMBAD or NED objects for each ALMA observation by taking the object that has the best combination of having a close name to the ALMA source name given by the PI, being a strong source and finally of being close to the RA/Dec position given by the PI.',
          elasticParserType: 'sky-object-types'
        },
        {
          id: 'publicData',
          label: 'Public data only',
          type: 'switch',
          elasticParserType: 'public-data'
        },
        {
          id: 'calibratorObservations',
          label: 'Calibration observations',
          type: 'switch',
          elasticParserType: 'calibration-data'
        }
      ]
    }
  ];
}
